// Las listas de los paneles del chat del agente: Historial de chats, Memoria y Consumo —
// ADR-2026-09-08-el-chat-toma-el-motor-del-asistente.
//
// Vivían inline en `AsistenteMode` (dos JSX de ~30 líneas). Las usa también el Chat
// ("Empleado IA") desde que tomó el motor del asistente, así que están acá UNA vez.
// Son presentacionales: reciben los datos y los callbacks, no cargan nada. Quien las
// monta decide cuándo pedir la lista (el asistente al abrir la hoja; el Chat, igual).

import React from 'react';
import { Trash2 } from 'lucide-react';
import { fechaDelChat, type AgentChatSummary } from '../../agentChatApi';
import type { AgentMemory } from '../../agentMemoryApi';

/** Fecha relativa cortita para el historial. */
export function fechaRelativa(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return `hace ${days} d`;
  return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
}

const Aviso: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[13.5px] text-[#3C3C43]/55 text-center py-8 px-4 leading-snug">
    {children}
  </p>
);

/** Lista del historial (cargando / vacío / items).
 *
 *  `onBorrar` es OPCIONAL: sin él la fila es un botón pelado, que es como la usa el
 *  Chat. Con él aparece el tacho —y sólo en las filas donde `puedeBorrar` diga que sí:
 *  el historial es POR EMPRESA y nadie tiene que poder borrar los chats de un compañero
 *  (ADR-2026-09-11-el-borrado-del-historial-no-cruza-usuarios).
 *
 *  Con tacho la fila deja de ser un `<button>`: uno anidado en otro es HTML inválido y
 *  el click del de adentro burbujea al de afuera — borrar abriría el chat recién
 *  borrado. Por eso es un div con dos botones hermanos. */
export const ListaHistorialChats: React.FC<{
  chats: AgentChatSummary[];
  cargando: boolean;
  onElegir: (chatId: string) => void;
  onBorrar?: (chat: AgentChatSummary) => void;
  puedeBorrar?: (chat: AgentChatSummary) => boolean;
  /** "de Juli" al lado de la fecha, para los chats que otro compartió con la empresa
   *  (ADR-2026-10-13-chats-compartidos-con-la-empresa). `null` = sin rótulo. */
  autorDe?: (chat: AgentChatSummary) => string | null;
}> = ({ chats, cargando, onElegir, onBorrar, puedeBorrar, autorDe }) => {
  if (cargando) return <Aviso>Cargando…</Aviso>;
  if (chats.length === 0) return <Aviso>Todavía no hay chats.</Aviso>;
  return (
    <div className="flex flex-col gap-0.5">
      {chats.map((c) => {
        const conTacho = !!onBorrar && (puedeBorrar?.(c) ?? true);
        const autor = autorDe?.(c) ?? null;
        const titulo = (
          <>
            <p className="text-[14.5px] font-medium text-brand-black/90 truncate">
              {c.title || 'Chat'}
            </p>
            <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5 truncate">
              {fechaRelativa(fechaDelChat(c))}
              {autor ? ` · de ${autor}` : ''}
            </p>
          </>
        );
        if (!conTacho) {
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onElegir(c.id)}
              className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
            >
              {titulo}
            </button>
          );
        }
        return (
          <div
            key={c.id}
            className="group flex items-center rounded-xl hover:bg-neutral-50 transition-colors"
          >
            <button
              type="button"
              onClick={() => onElegir(c.id)}
              className="flex-1 min-w-0 text-left px-3 py-2.5 active:opacity-60 transition-opacity"
            >
              {titulo}
            </button>
            {/* En desktop aparece al pasar por encima: es destructivo y sin deshacer,
                así que no tiene por qué estar a un click todo el tiempo. En mobile no
                hay hover, así que va siempre visible y atenuado. */}
            <button
              type="button"
              onClick={() => onBorrar!(c)}
              aria-label={`Borrar "${c.title || 'Chat'}"`}
              className="shrink-0 mr-1.5 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-red-500 hover:bg-red-50 transition-all md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

/** Lista de memorias (cargando / vacío / items con borrar). */
export const ListaMemorias: React.FC<{
  memorias: AgentMemory[];
  cargando: boolean;
  onBorrar: (id: string) => void;
}> = ({ memorias, cargando, onBorrar }) => {
  if (cargando) return <Aviso>Cargando…</Aviso>;
  if (memorias.length === 0) {
    return (
      <Aviso>
        Todavía no hay nada guardado. El asistente va a recordar tus preferencias y datos
        de tu marca a medida que charlen.
      </Aviso>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {memorias.map((m) => (
        <div
          key={m.id}
          className="group flex items-start gap-2 px-3 py-2.5 rounded-xl hover:bg-neutral-50 transition-colors"
        >
          <span className="flex-1 min-w-0 text-[14px] text-brand-black/90 leading-snug">
            {m.content}
          </span>
          <button
            type="button"
            onClick={() => onBorrar(m.id)}
            aria-label="Borrar memoria"
            className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg text-[#3C3C43]/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

/** El consumo del chat actual: créditos del agente más las generaciones. */
export const ResumenConsumo: React.FC<{
  creditos: number | null;
  cargando: boolean;
}> = ({ creditos, cargando }) => (
  <div className="px-3 pb-3 pt-1">
    <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
      Consumo de este chat
    </p>
    <div className="flex items-center gap-2 mt-2">
      <img
        src="/images/token_icon.png"
        alt=""
        aria-hidden="true"
        className="w-6 h-6 object-contain"
      />
      <span className="text-[26px] font-bold text-brand-primary leading-none">
        {cargando ? '…' : (creditos ?? 0)}
      </span>
      <span className="text-[13px] font-medium text-neutral-500">
        {(creditos ?? 0) === 1 ? 'crédito' : 'créditos'}
      </span>
    </div>
    <p className="text-[12.5px] text-[#3C3C43]/55 mt-1.5 leading-snug">
      Incluye el costo del asistente y las imágenes generadas en esta conversación.
    </p>
  </div>
);
